// Pure helpers for media URL handling. Kept in a separate file from
// `axios_helper.js` so the latter's axios import doesn't leak into
// unit tests (axios uses ESM syntax that CRA's Jest setup skips
// transforming).
//
// The expected base URL for the file-serving controller. Pulled from
// the backend's `/files/**` Spring mapping. Mirrors the production/
// dev switch in `axios_helper.js`: in production we hit the public
// HTTPS domain, in dev we hit the local Spring Boot port. Falls back
// to localhost so unit tests and SSR keep working.
const isProduction = process.env.REACT_APP_ENV === 'production'
  || (typeof window !== 'undefined' && window.location.protocol === 'https:');
export const API_BASE_URL = isProduction
  ? (process.env.REACT_APP_API_URL || 'https://globememories.com')
  : (process.env.REACT_APP_API_URL || 'http://localhost:8080');

export const BASE_FILES_URL = `${API_BASE_URL}/files`;

// The wizard uses this path only as a temporary placeholder while a new
// trip is being created. It must never be exposed as a real poster or cover
// URL on a feed/card because the file is intentionally not persisted.
export const isPlaceholderMediaPath = (path) => {
  const value = String(path || '').split(/[?#]/, 1)[0];
  return /(?:^|\/)trip-photos\/placeholder\.jpg$/i.test(value);
};

/**
 * Responsive JPEG variants generated by the backend.
 *
 * These are intentionally ordinary JPEG files (rather than WebP URLs):
 * older production deployments only generated JPEG thumbnails and a
 * browser request for a missing `.webp` would add a needless 404/retry.
 */
export const THUMB_WIDTHS = [320, 640, 1024];

const DEFAULT_THUMB_WIDTH = 640;

const isLegacyMediaHost = (hostname) => (
  hostname === 'localhost'
  || hostname === '127.0.0.1'
  || hostname === '178.238.231.215'
);

/** Split a media reference while preserving its cache-busting suffix. */
function splitReference(path) {
  const raw = String(path || '').trim();
  if (!raw) return null;
  const match = raw.match(/^([^?#]*)([?#].*)?$/);
  return { path: match?.[1] || '', suffix: match?.[2] || '' };
}

/**
 * Return the path relative to `/files`, or null for an external CDN URL.
 * This lets the helper accept both backend paths and URLs already returned
 * by older API versions.
 */
function relativeFilesPath(path) {
  const ref = splitReference(path);
  if (!ref?.path) return null;
  const value = ref.path.replace(/^\/+/, '');

  if (/^https?:\/\//i.test(ref.path)) {
    try {
      const url = new URL(path);
      const base = new URL(BASE_FILES_URL);
      const filesPrefix = `${base.pathname.replace(/\/$/, '')}/`;
      if (!url.pathname.startsWith(filesPrefix)) return null;
      if (url.origin !== base.origin && !isLegacyMediaHost(url.hostname)) return null;
      return {
        path: url.pathname.slice(filesPrefix.length),
        suffix: `${url.search}${url.hash}`,
      };
    } catch (_) {
      return null;
    }
  }

  const withoutFilesPrefix = value.replace(/^files\//i, '');
  if (!withoutFilesPrefix || withoutFilesPrefix.startsWith('thumbs/')) {
    return { path: withoutFilesPrefix, suffix: ref.suffix };
  }
  return { path: withoutFilesPrefix, suffix: ref.suffix };
}

/** Strip the file extension from a relative path. */
function stripExt(path) {
  const slash = path.lastIndexOf('/');
  const dot = path.lastIndexOf('.');
  return dot > slash ? path.substring(0, dot) : path;
}

/** Remove a generated responsive suffix before addressing its siblings. */
function stripVideoVariantSuffix(path) {
  return path.replace(/-(?:mobile|desktop)$/i, '');
}

// Older wizard submissions accidentally persisted the temporary staging
// folder in the trip row. Staged files are promoted to trip-videos/ after a
// successful commit, so clients must canonicalise that legacy reference
// before making any playback, poster or retry request. This also prevents a
// stale database row from producing repeated 404s on iOS and Android.
function canonicalTripVideoPath(path) {
  return String(path || '').replace(
    /^trip-videos\/staged\/[^/]+\//i,
    'trip-videos/',
  );
}

/**
 * Convert a relative fileUrl returned by the backend (e.g.
 * "trip-photos/abc.jpg") to a full public URL suitable for use in
 * <img src> or <video src>.
 *
 * Returns null for falsy input. Passes through already-absolute
 * URLs unchanged. When `bustCache` is true a `?v=<timestamp>`
 * query string is appended so the browser doesn't serve a stale
 * cached image after the user uploads a new photo of the same
 * path.
 */
export const toFullMediaUrl = (path, { bustCache = false } = {}) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) {
    const legacyRef = relativeFilesPath(path);
    if (legacyRef?.path) {
      const current = `${BASE_FILES_URL}/${canonicalTripVideoPath(legacyRef.path)}${legacyRef.suffix}`;
      return bustCache ? `${current}${current.includes('?') ? '&' : '?'}v=${Date.now()}` : current;
    }
    return bustCache ? `${path}${path.includes('?') ? '&' : '?'}v=${Date.now()}` : path;
  }
  const cleanPath = canonicalTripVideoPath(
    String(path).replace(/^\/+/, '').replace(/^files\//i, ''),
  );
  const url = `${BASE_FILES_URL}/${cleanPath}`;
  return bustCache ? `${url}?v=${Date.now()}` : url;
};

const VIDEO_MEDIA_PATTERN = /\.(mp4|webm|mov|m4v|avi|mkv)(?:[?#].*)?$/i;

function mediaPathKey(path) {
  const raw = String(path || '').split(/[?#]/, 1)[0].trim();
  if (!raw) return '';
  return raw
    .replace(/^https?:\/\/[^/]+\/files\//i, '')
    .replace(/^\/?files\//i, '')
    .replace(/^\/+/, '');
}

/**
 * Resolve a trip hero consistently for every list/card surface.
 *
 * The API has both the legacy `tripPhoto` field and the newer explicit
 * `coverMediaPath`/`coverMediaType` pair. During older edits one of those
 * values can be missing for a short time, so every caller must apply the
 * same fallback rules instead of deciding independently whether to render
 * an <img> or a <video>.
 */
export const resolveTripCoverMedia = ({
  coverMediaPath,
  coverMediaType,
  photos = [],
  videos = [],
  fallbackPhoto = null,
} = {}) => {
  const photoPaths = (Array.isArray(photos) ? photos : [])
    .filter((path) => path && !isPlaceholderMediaPath(path));
  const videoPaths = (Array.isArray(videos) ? videos : [])
    .filter((path) => path && !isPlaceholderMediaPath(path));
  const explicitPath = coverMediaPath && !isPlaceholderMediaPath(coverMediaPath)
    ? coverMediaPath
    : null;
  const explicitType = String(coverMediaType || '').toLowerCase();
  const explicitKey = mediaPathKey(explicitPath);
  const belongsToVideos = explicitKey && videoPaths.some((path) => mediaPathKey(path) === explicitKey);
  const looksLikeVideo = VIDEO_MEDIA_PATTERN.test(String(explicitPath || ''));
  const hasVideoOnlyFallback = !explicitPath && photoPaths.length === 0 && videoPaths.length > 0;
  const type = explicitType === 'video' || belongsToVideos || looksLikeVideo || hasVideoOnlyFallback
    ? 'video'
    : 'photo';
  const path = explicitPath
    || (type === 'video' ? videoPaths[0] : (photoPaths[0] || fallbackPhoto));

  if (!path || isPlaceholderMediaPath(path)) {
    return { type, path: null, url: null };
  }
  return { type, path, url: toFullMediaUrl(path) || path };
};

/**
 * Build a thumbnail URL for a backend media path. The backend stores the
 * thumbnail as JPEG regardless of the original extension, so `photo.png`
 * correctly maps to `thumbs/640/.../photo.jpg`.
 */
export const toFullMediaThumb = (path, { width = DEFAULT_THUMB_WIDTH, bustCache = false } = {}) => {
  if (!path) return null;
  const ref = relativeFilesPath(path);
  if (!ref || !ref.path || ref.path.startsWith('thumbs/')) {
    return toFullMediaUrl(path, { bustCache });
  }
  const safeWidth = THUMB_WIDTHS.includes(Number(width)) ? Number(width) : DEFAULT_THUMB_WIDTH;
  const thumb = `${BASE_FILES_URL}/thumbs/${safeWidth}/${stripExt(ref.path)}.jpg${ref.suffix}`;
  return bustCache ? `${thumb}${thumb.includes('?') ? '&' : '?'}v=${Date.now()}` : thumb;
};

/** Build the responsive `srcset` consumed by ProgressiveImg. */
export const toFullMediaSrcSet = (path, { widths = THUMB_WIDTHS, bustCache = false } = {}) => {
  if (!path) return null;
  const ref = relativeFilesPath(path);
  // External CDN URLs (and already-generated thumb URLs) have no local
  // variants we can address safely, so let the browser use one URL.
  if (!ref?.path || ref.path.startsWith('thumbs/')) return null;
  const entries = widths
    .map((width) => {
      const url = toFullMediaThumb(path, { width, bustCache });
      return url ? `${url} ${width}w` : null;
    })
    .filter(Boolean);
  return entries.length ? entries.join(', ') : null;
};

/**
 * Return the optimised video variant URL generated by the backend.
 *
 * The canonical desktop file keeps the original basename and the mobile
 * encode adds `-mobile.mp4`. Legacy uploads keep their original extension;
 * in that case both variants are addressed as MP4 files. External URLs and
 * blob URLs are left untouched because we cannot derive a sibling file for
 * them safely.
 */
export const toVideoVariantUrl = (path, variant = 'mobile') => {
  if (!path) return null;
  const ref = relativeFilesPath(path);
  if (!ref?.path || !/^trip-videos\//i.test(ref.path)) return null;

  const lower = ref.path.toLowerCase();
  if (!/\.(mp4|webm|mov|avi|mkv)$/.test(lower)) return null;
  const base = stripVideoVariantSuffix(stripExt(canonicalTripVideoPath(ref.path)));
  const extension = '.mp4';
  const candidate = variant === 'mobile'
    ? `${base}-mobile${extension}`
    : `${base}${extension}`;
  return `${BASE_FILES_URL}/${candidate}${ref.suffix}`;
};

/**
 * Resolve the poster generated from a stored video. It is intentionally not
 * derived from a trip cover/photo: this URL always points to a frame from
 * the video itself.
 */
export const toVideoPosterUrl = (
  path,
  { bustCache = false, preserveStaging = false } = {},
) => {
  if (!path) return null;
  const ref = relativeFilesPath(path);
  if (!ref?.path || !/^trip-videos\//i.test(ref.path)) return null;
  const lower = ref.path.toLowerCase();
  if (!/\.(mp4|webm|mov|avi|mkv)$/.test(lower)) return null;
  // Wizard previews may still point to a file in the temporary staging
  // folder. Public trip pages use the permanent path by default, while the
  // wizard can explicitly keep the staging folder until the final commit.
  const videoPath = preserveStaging ? ref.path : canonicalTripVideoPath(ref.path);
  const poster = `${BASE_FILES_URL}/${stripVideoVariantSuffix(stripExt(videoPath))}-poster.jpg${ref.suffix}`;
  return bustCache ? `${poster}${poster.includes('?') ? '&' : '?'}v=${Date.now()}` : poster;
};

/**
 * Resolve a user's avatar URL regardless of which field the caller
 * uses. The codebase historically exposes two aliases:
 *   - `profilePhoto`  — the canonical field returned by the backend
 *   - `profilePicture` — a frontend alias some pages introduced
 *
 * `profilePhoto` wins when both are set. The returned URL is
 * always the fully-qualified public URL.
 */
export const getUserAvatar = (user, { bustCache = false } = {}) => {
  if (!user) return null;
  const path = user.profilePhoto || user.profilePicture;
  return toFullMediaUrl(path, { bustCache });
};
